import React, { memo } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import {
  generateSalesGaugeData,
  generateRangeChartData,
  cleanPersonaAndBatchData,
  generateRadarChartData,
  brandDashboardRadarChartSubjects,
  makeDataStructureForDataTabel,
  socialValueDimensionsExplanations,
  getProductBenefitsSubHeading,
  getCompetitiveMapText,
  getTop3SocialCauses,
  getIndustryAverage,
  getValuesMatchText,
  getTotalMatchText,
  getProductBenefitsMatchText,
} from './helpers';
import { getIndustryName, truncateLegendText } from '../../../../helperFunctions';

import { getTableDataCriteria } from './exampleData';
import useViewportWidth from '../../../../myHooks/useViewportWidth';

import DashDetailsWrapper from '../../../../myComponents/dashDetailsWrapper';
import DashSectionExplanation from '../../../../myComponents/dashSectionExplanation';
import ContentSwitcher from '../../../../myComponents/contentSwitcher';
import SalesGauge from '../../../../myComponents/chart/SalesGaugeChart';
import RangeChart from '../../../../myComponents/chart/RangeChart';
import DataInfoTable from '../../../../myComponents/chart/dataInfoTable';
import RadarChart from '../../../../myComponents/chart/RadarChart';
import ScatterChart from '../../../../myComponents/chart/ScatterChart';
import Watermark from '../../../../myComponents/watermark';
import DataInfoTableSm from '../../../../myComponents/chart/dataInfoTableSm';

const useStyles = makeStyles(theme => ({
  textStyle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    textAlign: 'center',
    marginTop: '10px',
  },
  radarChartWrapper: {
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  productBenefitsWrapper: {
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  rangeChartContainer: {
    marginTop: '50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    width: '100%',
    columnGap: '24px',
    rowGap: '32px',

    '@media (max-width: 1260px)': {
      justifyContent: 'center',
    },
  },
  rangeChartWrapper: {
    maxWidth: '500px',
    margin: '0 0 32px 0',
    width: '100%',
    '@media (max-width: 1240px)': {
      maxWidth: '600px',
    },
    '@media (max-width: 768px)': {
      maxWidth: '100%',
      width: '100%',
      margin: '0px 0px 60px 0px',
      padding: 0,
    },
  },
  supportRangeChartWrapper: {
    maxWidth: '500px',
    margin: '0 0 24px 0',
    width: '100%',
    '@media (max-width: 1240px)': {
      maxWidth: '600px',
    },
    '@media (max-width: 768px)': {
      maxWidth: '100%',
      width: '100%',
      margin: '0px 0px 30px 0px',
      padding: 0,
    },
  },
  socialValueDimensionFlexContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    width: '100%',
    '@media (max-width: 1090px)': {
      justifyContent: 'center',
    },
    '@media (max-width: 768px)': {
      flexDirection: 'column !important',
      flexWrap: 'wrap',
    },
  },
  radarChartContainerWidth: {
    '@media (max-width: 768px)': {
      maxWidth: '100%',
      width: '100%',
      flex: 'unset',
    },
  },
  brandPerformanceResponsive: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    width: '100%',

    '@media (max-width: 1265px)': {
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  competitiveMapExplanationContainer: {
    marginTop: '30px',
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    padding: '0 5% 0 5%',
    width: '100%',

    '@media (max-width: 1244px)': {
      justifyContent: 'center',
    },
  },
  socialCausesRadarChartContent: {
    flex: 1,
    margin: '30px auto 0 auto',
    maxWidth: '650px',
    '@media (max-width: 620px)': {
      width: '100%',
    },
  },
  socialCausesRadarChartText: {
    marginLeft: '0',
    marginBottom: '24px',
    color: 'lightgray',
    fontWeight: 300,
    fontSize: '16px',
    textAlign: 'left',
    '@media (max-width: 768px)': {
      maxWidth: '420px',
      margin: '0 auto',
    },
  },
  socialCausesRadarChartDashExp: {
    marginTop: '24px',
    marginLeft: '0',

    '@media (max-width: 768px)': {
      maxWidth: '420px',
      margin: '40px auto 0px auto',
    },
  },
  scatterChartContainer: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: '#254157',
    padding: '40px 40px 100px 40px',

    '@media (max-width: 1000px)': {
      padding: '40px 0px 100px 0px',
    },
    '@media (max-width: 870px)': {
      transform: 'scale(0.8)',
    },
    '@media (max-width: 700px)': {
      padding: '0',
    },
    '@media (max-width: 650px)': {
      transform: 'scale(0.7)',
    },
    '@media (max-width: 550px)': {
      transform: 'scale(0.6)',
      marginTop: '-30px',
    },
    '@media (max-width: 470px)': {
      transform: 'scale(0.5)',
      marginTop: '-80px',
      marginBottom: '-50px',
    },
    '@media (max-width: 400px)': {
      transform: 'scale(0.47)',
    },
  },
  issuesSupportSubHeading: {
    color: 'white',
    fontSize: '25px',
    fontWeight: '200',
    margin: '0',

    '@media (max-width: 768px)': {
      fontSize: '20px',
    },
  },
}));

const ChartCalculations = ({ data, brandPersona, isSelectedSample }) => {
  const classes = useStyles();
  const viewportWidth = useViewportWidth();
  const sampleLabel = isSelectedSample ? 'SELECTED SAMPLE' : 'BUYER PERSONA';
  const isReportExport = window.location.pathname.includes('/report-export/');

  const tableDataCriteria = getTableDataCriteria();
  const cleanBatchDataInfo = {
    ...data.most_occurring,
    Age: Math.round(data.average.Age),
  };
  const cleanPersonaInfo = {
    ...brandPersona,
    Country: brandPersona.country,
  };
  const [personaTableData, batchTableData] = cleanPersonaAndBatchData(cleanPersonaInfo, cleanBatchDataInfo);
  const tableDataInfo = makeDataStructureForDataTabel(tableDataCriteria, personaTableData, batchTableData);

  let generatedSalesGaugeData = generateSalesGaugeData('brand', data.match);
  let generatedRangeChartSocial = generateRangeChartData(
    data.brand_persona_data,
    data.average.SocialValues,
    'social',
    'brand',
  );

  let generatedRangeChartProduct = generateRangeChartData(brandPersona, data.average.PersonalProduct, 'product');

  let generatedRangeChartSupport = generateRangeChartData(brandPersona, data.average.PersonalIssuesSupport, 'support');

  const brandMatchMapValues = [
    data.match.IE_Focus_Match,
    data.match.Issues_Match,
    data.match.CausesSupport_Match,
    data.match.Ideology_Match,
    data.match.Innovation_Match,
    data.match.Product_Match,
  ];
  const socialValueDimensionsData = generateRadarChartData(
    'brand',
    'single',
    brandMatchMapValues,
    brandDashboardRadarChartSubjects.brandMatch,
  );

  const multipleRadarChartDataLabels = [brandPersona.Name, sampleLabel];
  const multipleRadarChartData = generateRadarChartData(
    'brand',
    'multiple',
    [data.average.PersonalIssues, brandPersona],
    brandDashboardRadarChartSubjects.socialIssues,
    multipleRadarChartDataLabels,
  );

  let multipleRadarChartDataOrdered = [];
  const radarChartTemplate = [
    'State of the Economy',
    'Free Healthcare',
    'Violent Crime',
    'World Peace',
    'Gun Laws',
    'Race and Ethnic Inequality',
    'Gender Inequality',
    'Immigration',
    'Economic Inequality',
    'Climate Change',
    'Corporate Corruption',
  ];

  radarChartTemplate.forEach(subject => {
    multipleRadarChartData.forEach(el => {
      if (el.subject == subject) {
        multipleRadarChartDataOrdered = [...multipleRadarChartData, { ...el }];
        return;
      }
    });
  });

  multipleRadarChartDataOrdered.pop();

  const top3SocialCauses = getTop3SocialCauses(multipleRadarChartData, sampleLabel);
  const productBeneitsSubHeading = getProductBenefitsSubHeading();

  //Competitive map
  const scatterChartWidth = viewportWidth > 1100 ? 900 : 750;
  const competitiveExists = data.companies_match ? true : false;
  const generatedSalesGaugeDataText = [];
  let competitiveMapText;

  if (competitiveExists) {
    competitiveMapText = getCompetitiveMapText();
    const valuesIndustryAverage = getIndustryAverage('values', data.companies_match);
    const valuesMatchText = {
      value: valuesIndustryAverage.toFixed(2) * 100,
      text: getValuesMatchText(data.match.Values_Match, valuesIndustryAverage),
    };
    generatedSalesGaugeDataText.push(valuesMatchText);

    const totalIndustryAverage = getIndustryAverage('total', data.companies_match);
    const totalMatchText = {
      value: totalIndustryAverage.toFixed(2) * 100,
      text: getTotalMatchText(data.match.Total_Match, totalIndustryAverage),
    };
    generatedSalesGaugeDataText.push(totalMatchText);

    const productIndustryAverage = getIndustryAverage('product', data.companies_match);
    const productBenefitsMatchText = {
      value: productIndustryAverage.toFixed(2) * 100,
      text: getProductBenefitsMatchText(data.match.Product_Match, productIndustryAverage),
    };
    generatedSalesGaugeDataText.push(productBenefitsMatchText);
  }

  const getChartSize = width => {
    return width > 1025 ? 200 : width > 768 ? 150 : width > 520 ? 120 : 100;
  };

  const getDimensionsChartSize = width => {
    return width > 1500 ? 250 : width > 1025 ? 200 : width > 768 ? 150 : width > 520 ? 120 : 100;
  };

  const industryName = getIndustryName(brandPersona.Industry);

  const salesGaugeUniqueIds = ['brand-performance', 'social-value-dimensions'];

  const _companiesMatch = data?.companies_match;

  // if product > 70 || values > 80
  let displayLargeCompetitiveMatchMap = false;

  if (competitiveExists) {
    _companiesMatch.forEach(companyMatchItem => {
      if (companyMatchItem.match.product * 100 > 70 || companyMatchItem.match.values * 100 > 80) {
        displayLargeCompetitiveMatchMap = true;
      }
    });

    if (data.match.Product_Match * 100 > 69 || data.match.Values_Match * 100 > 79) {
      displayLargeCompetitiveMatchMap = true;
    }
  }

  return (
    <Box>
      {/* Brand Performance */}
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Brand Performance',
          color: '#19989B',
        }}>
        <Box className={classes.brandPerformanceResponsive}>
          {viewportWidth <= 768 && (
            <Box key={'1-sm'}>
              <SalesGauge
                salesGaugeData={generatedSalesGaugeData.brandPerformance[1].data}
                chartId={salesGaugeUniqueIds[0]}
                size={'medium'}
                color="#03DAC5"
                chartTypeColor="#6C757D"
                chartValueColor="black"
                body1={{
                  headingColor: 'black',
                  headingText: generatedSalesGaugeData.brandPerformance[1].body1.headingText,
                  bodyColor: 'gray',
                  bodyText: generatedSalesGaugeData.brandPerformance[1].body1.bodyText,
                }}
                body2={
                  competitiveExists
                    ? {
                        headingColor: 'black',
                        headingText: `Industry Average - ${generatedSalesGaugeDataText[1].value}%`,
                        bodyColor: 'black',
                        bodyText: generatedSalesGaugeDataText[1].text,
                      }
                    : null
                }
              />
            </Box>
          )}
          {generatedSalesGaugeData.brandPerformance.map((salesGaugeData, index) => {
            return index !== 1 || viewportWidth > 768 ? (
              <Box key={index}>
                <SalesGauge
                  salesGaugeData={salesGaugeData.data}
                  chartId={salesGaugeUniqueIds[0] + index}
                  size={index === 1 && viewportWidth > 768 ? 'large' : 'medium'}
                  color="#03DAC5"
                  chartTypeColor="#6C757D"
                  chartValueColor="black"
                  body1={{
                    headingColor: 'black',
                    headingText: salesGaugeData.body1.headingText,
                    bodyColor: 'gray',
                    bodyText: salesGaugeData.body1.bodyText,
                  }}
                  body2={
                    competitiveExists
                      ? {
                          headingColor: 'black',
                          headingText: `Industry Average - ${generatedSalesGaugeDataText[index].value}%`,
                          bodyColor: 'black',
                          bodyText: generatedSalesGaugeDataText[index].text,
                        }
                      : null
                  }
                />
              </Box>
            ) : null;
          })}
        </Box>
      </DashDetailsWrapper>

      {/* Social Value Dimensions */}
      <DashDetailsWrapper
        backgroundColor="#254157"
        expandColor="white"
        heading1={{
          text: 'Social Value Dimensions',
          color: 'white',
        }}>
        <ContentSwitcher
          headingText="BRAND MATCH MAP"
          headingColor={isReportExport ? '#111827' : 'white'}
          type="brand"
          content1={
            <Box className={classes.brandPerformanceResponsive} style={{ justifyContent: 'space-around' }}>
              {generatedSalesGaugeData.socialValueDimensions.map((salesGaugeData, index) => {
                return (
                  <Box key={index}>
                    <SalesGauge
                      salesGaugeData={salesGaugeData.data}
                      chartId={salesGaugeUniqueIds[1] + index}
                      size="medium"
                      color="#03DAC5"
                      chartTypeColor="white"
                      chartValueColor="white"
                      body1={{
                        headingColor: 'white',
                        headingText: salesGaugeData.body1.headingText,
                        bodyColor: 'lightgray',
                        bodyText: salesGaugeData.body1.bodyText,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          }
          content2={
            <Box className={classes.socialValueDimensionFlexContainer}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="flex-start">
                {socialValueDimensionsExplanations.map((explanation, index) => (
                  <Box marginBottom="20px" maxWidth="350px" key={index}>
                    <DashSectionExplanation
                      heading={explanation.heading}
                      headingColor={isReportExport ? '#111827' : 'white'}
                      body={explanation.body}
                      bodyColor={isReportExport ? '#4B5563' : 'lightgray'}
                    />
                  </Box>
                ))}
              </Box>
              <Box
                className={classes.radarChartContainerWidth}
                flex={1}
                margin="auto"
                height={viewportWidth > 1500 ? '600px' : '480px'}
                maxWidth={viewportWidth > 1500 ? '750px' : '600px'}>
                <RadarChart
                  personaType="brand"
                  type="single"
                  data={socialValueDimensionsData}
                  size={getDimensionsChartSize(viewportWidth)}
                  tickTextColor={isReportExport ? '#6B7280' : '#90A0B0'}
                />
              </Box>
            </Box>
          }
        />
      </DashDetailsWrapper>

      {/* Brand Persona & Consumer Demographics */}
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Brand Persona & Consumer Demographics',
          color: '#19989B',
        }}>
        {viewportWidth > 1024 ? (
          <DataInfoTable
            data={tableDataInfo}
            personaName={brandPersona.Name}
            type="brand"
            isBuyerComparison={!isSelectedSample}
            sampleLabel={sampleLabel}
          />
        ) : (
          <DataInfoTableSm
            data={tableDataInfo}
            personaName={brandPersona.Name}
            type="brand"
            isBuyerComparison={!isSelectedSample}
            sampleLabel={sampleLabel}
          />
        )}
      </DashDetailsWrapper>

      {/* Social Values, Social Issues & Supported Causes */}
      <DashDetailsWrapper
        backgroundColor="#254157"
        expandColor="white"
        heading1={{
          text: 'Social Values, Social Issues & Supported Causes',
          color: 'white',
        }}>
        <ContentSwitcher
          headingText="CAUSES SUPPORT"
          headingColor={isReportExport ? '#111827' : 'white'}
          type="brand"
          content1={
            <Box className={classes.brandPerformanceResponsive}>
              <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="flex-start">
                {generatedRangeChartSocial.map((socialRangeChartData, index) => (
                  <Box marginBottom="20px" maxWidth="420px" key={index}>
                    <RangeChart
                      mainLineColor="#667A89"
                      middleLineColor="#EFEFEF"
                      leftText={{
                        text: socialRangeChartData.leftText,
                        color: isReportExport ? '#374151' : 'white',
                      }}
                      rightText={{
                        text: socialRangeChartData.rightText,
                        color: isReportExport ? '#374151' : 'white',
                      }}
                      valueColor="lightgray"
                      heading={socialRangeChartData.heading}
                      headingColor={isReportExport ? '#111827' : 'white'}
                      body={{
                        text: socialRangeChartData.body,
                        color: 'lightgray',
                      }}
                      value1={socialRangeChartData.personaValue}
                      value2={socialRangeChartData.batchValue}
                      type="brand"
                    />
                  </Box>
                ))}
              </Box>
              <Box className={classes.socialCausesRadarChartContent}>
                <p className={classes.socialCausesRadarChartText} style={{ color: isReportExport ? '#4B5563' : 'lightgray' }}>
                  This chart compares consumers' and brands' positions regarding the most important 11 social issues that are
                  commonly used in political research and social sciences.
                </p>
                <Box className={classes.radarChartWrapper} height="500px" width="700px">
                  <RadarChart
                    type="multiple"
                    data={multipleRadarChartDataOrdered}
                    size={getChartSize(viewportWidth)}
                    AConfig={{
                      name: truncateLegendText(multipleRadarChartDataLabels[0], viewportWidth),
                      color: '#19989B',
                    }}
                    BConfig={{
                      name: multipleRadarChartDataLabels[1],
                      color: '#CE5378',
                    }}
                    tickTextColor={isReportExport ? '#6B7280' : '#90A0B0'}
                  />
                </Box>
                <Box className={classes.socialCausesRadarChartDashExp}>
                  <DashSectionExplanation
                    heading={'Issues That Consumers Most Care About:'}
                    headingColor={isReportExport ? '#111827' : 'white'}
                    body={
                      <span>
                        {top3SocialCauses.map((cause, index) => (
                          <p key={index} style={{ marginBottom: '1px', fontSize: '15px' }}>
                            - {cause.subject} -<strong> {cause[sampleLabel]}%</strong>
                          </p>
                        ))}
                      </span>
                    }
                    bodyColor={isReportExport ? '#4B5563' : 'lightgray'}
                  />
                </Box>
              </Box>
            </Box>
          }
          content2={
            <Box marginBottom="30px">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <p className={classes.issuesSupportSubHeading} style={{ color: isReportExport ? '#111827' : 'white' }}>Support for 18 Leading Social Causes</p>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  gridRowGap="10px">
                  <Box display="flex" alignItems="center">
                    <Box
                      style={{
                        backgroundColor: '#CE5378',
                        width: '15px',
                        minWidth: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        boxShadow: '#1e1d1d 0px 4px 6px 0px',
                        marginRight: '10px',
                      }}></Box>
                    <p
                      style={{
                        color: isReportExport ? '#111827' : '#F3F3F3',
                        fontWeight: 300,
                        fontSize: '14px',
                        textAlign: 'left',
                        margin: '0',
                      }}>
                      {sampleLabel}
                    </p>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Box
                      style={{
                        backgroundColor: '#19989B',
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        boxShadow: '#1e1d1d 0px 4px 6px 0px',
                        marginRight: '10px',
                      }}></Box>
                    <p
                      style={{
                        color: isReportExport ? '#111827' : '#F3F3F3',
                        fontWeight: 300,
                        fontSize: '14px',
                        textAlign: 'left',
                        margin: '0',
                      }}>
                      {truncateLegendText(brandPersona.Name, viewportWidth)}
                    </p>
                  </Box>
                </Box>
              </Box>
              <Box className={classes.rangeChartContainer} data-report-chart-grid={isReportExport ? 'true' : undefined}>
                {generatedRangeChartSupport.map((issuesSupportRangeChartData, index) => (
                  <Box className={classes.supportRangeChartWrapper} key={index} data-report-chart-item={isReportExport ? 'true' : undefined}>
                    <RangeChart
                      mainLineColor={isReportExport ? '#E5E7EB' : 'rgba(255, 255, 255, 0.3)'}
                      middleLineColor={isReportExport ? '#D1D5DB' : 'white'}
                      leftText={{
                        text: issuesSupportRangeChartData.leftText,
                        color: isReportExport ? '#374151' : 'white',
                      }}
                      rightText={{
                        text: issuesSupportRangeChartData.rightText,
                        color: isReportExport ? '#374151' : 'white',
                      }}
                      valueColor={isReportExport ? '#6B7280' : 'white'}
                      heading={issuesSupportRangeChartData.heading}
                      headingColor={isReportExport ? '#111827' : 'white'}
                      value1={issuesSupportRangeChartData.personaValue}
                      value2={issuesSupportRangeChartData.batchValue}
                      type="brand"
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          }
        />
      </DashDetailsWrapper>

      {/* Product Benefits */}
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Product Benefits',
          color: '#19989B',
        }}>
        <Box className={classes.productBenefitsWrapper}>
          <Box display="flex" alignItems="center" marginBottom="20px">
            <Box display="flex" alignItems="center" marginRight="20px">
              <Box
                style={{
                  backgroundColor: '#CE5378',
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  boxShadow: '0px 4px 3px lightgray',
                  marginRight: '10px',
                }}></Box>
              <p
                style={{
                  color: 'black',
                  fontWeight: 300,
                  fontSize: '16px',
                  textAlign: 'left',
                  margin: '0',
                }}>
                {sampleLabel}
              </p>
            </Box>
            <Box display="flex" alignItems="center">
              <Box
                style={{
                  backgroundColor: '#19989B',
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  boxShadow: '0px 4px 3px lightgray',
                  marginRight: '10px',
                }}></Box>
              <p
                style={{
                  color: 'black',
                  fontWeight: 300,
                  fontSize: '16px',
                  textAlign: 'left',
                  margin: '0',
                }}>
                {truncateLegendText(brandPersona.Name, viewportWidth)}
              </p>
            </Box>
          </Box>
          <p
            style={{
              color: '#A5A5A5',
              fontWeight: 300,
              fontSize: '16px',
              textAlign: 'left',
              margin: '0',
              maxWidth: '700px',
            }}>
            {productBeneitsSubHeading}
          </p>
          <Box className={classes.rangeChartContainer} data-report-chart-grid={isReportExport ? 'true' : undefined}>
            {generatedRangeChartProduct.map((productRangeChartData, index) => (
              <Box className={classes.rangeChartWrapper} key={index} data-report-chart-item={isReportExport ? 'true' : undefined}>
                <RangeChart
                  mainLineColor="#E5E6E6"
                  middleLineColor="#D9D9D9"
                  leftText={{
                    text: productRangeChartData.leftText,
                    color: '#6C757D',
                  }}
                  rightText={{
                    text: productRangeChartData.rightText,
                    color: '#6C757D',
                  }}
                  valueColor="gray"
                  body={{
                    text: productRangeChartData.body,
                    color: '#6C757D',
                  }}
                  value1={productRangeChartData.personaValue}
                  value2={productRangeChartData.batchValue}
                  type="brand"
                />
              </Box>
            ))}
          </Box>
        </Box>
      </DashDetailsWrapper>

      {/* Competitive Chart */}
      {competitiveExists && (
        <DashDetailsWrapper
          backgroundColor="#254157"
          expandColor="white"
          heading1={{
            text: `Competitive Map - ${industryName?.name}`,
            color: 'white',
          }}>
          <Box width="100%">
            <Box className={classes.scatterChartContainer}>
              <ScatterChart
                matches={{
                  targetMatch: {
                    product: data.match.Product_Match,
                    values: data.match.Values_Match,
                    name: brandPersona.Name,
                  },
                  otherCompaniesMatch: data.companies_match,
                }}
                insideLabels={competitiveMapText.chartLabels}
                XYAxisLabels={competitiveMapText.XYAxisLabels}
                chartHeightWidth={{
                  width: scatterChartWidth,
                  height: 550,
                }}
                large={displayLargeCompetitiveMatchMap}
              />
            </Box>
            <Box className={classes.competitiveMapExplanationContainer}>
              {competitiveMapText.explanations.map((explanation, index) => {
                return (
                  <Box marginBottom="20px" maxWidth="500px" key={index}>
                    <DashSectionExplanation
                      heading={explanation.heading}
                      headingColor={isReportExport ? '#111827' : 'white'}
                      body={explanation.body}
                      bodyColor={isReportExport ? '#4B5563' : 'lightgray'}
                    />
                  </Box>
                );
              })}
            </Box>
            <Watermark lightText={true} />
          </Box>
        </DashDetailsWrapper>
      )}
    </Box>
  );
};

export default memo(ChartCalculations);
